import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, MapPin, Truck, Award, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_SHIPPING_FROM = 60;
const COLISSIMO_FEE = 8.9;

export const CartPage = () => {
  const { items, updateQty, remove, subtotal, count } = useCart();
  return (
    <div className="min-h-screen bg-[#FDFAF4] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl text-[#5C2018] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
          Votre panier
        </h1>
        <p className="text-sm text-[#332724]/70 mb-8">{count} article{count > 1 ? 's' : ''}</p>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-[#B8922A]/20">
            <ShoppingBag size={48} className="text-[#B8922A]/40 mx-auto mb-4" />
            <p className="text-[#332724]/70 mb-6">Votre panier est vide.</p>
            <Link to="/boutique" className="btn-primary px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em]">
              Voir la boutique
            </Link>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-[#B8922A]/20 divide-y divide-[#B8922A]/10 mb-6">
              {items.map((i) => (
                <div key={i.sku} className="p-4 flex items-center gap-4" data-testid={`cart-item-${i.sku}`}>
                  <div className="flex-1">
                    <p className="font-medium text-[#5C2018]">{i.label}</p>
                    <p className="text-xs text-[#332724]/60">{i.unit_price.toFixed(2)}€ l'unité</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(i.sku, i.qty - 1)} className="w-7 h-7 rounded-full border border-[#B8922A]/40 flex items-center justify-center hover:bg-[#FDFAF4]"><Minus size={12} /></button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button onClick={() => updateQty(i.sku, i.qty + 1)} className="w-7 h-7 rounded-full border border-[#B8922A]/40 flex items-center justify-center hover:bg-[#FDFAF4]"><Plus size={12} /></button>
                  </div>
                  <p className="w-20 text-right font-medium text-[#5C2018]">{(i.unit_price * i.qty).toFixed(2)}€</p>
                  <button onClick={() => remove(i.sku)} className="text-[#5C2018]/50 hover:text-[#5C2018]"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-5 border border-[#B8922A]/30 flex items-center justify-between mb-6">
              <span className="text-sm text-[#332724]/70">Sous-total</span>
              <span className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{subtotal.toFixed(2)}€</span>
            </div>
            <Link to="/checkout" className="block w-full btn-primary px-6 py-4 rounded-full text-center text-xs uppercase tracking-[0.2em]" data-testid="cart-checkout-btn">
              Passer commande <ArrowRight size={14} className="inline ml-2" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const CheckoutPage = () => {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    delivery_mode: 'click_collect',
    delivery_address: '', delivery_city: '', delivery_zip: '',
    loyalty_email: '', notes: '', accept_cgv: false,
  });
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [loading, setLoading] = useState(false);

  const deliveryFee = form.delivery_mode === 'colissimo' && subtotal < FREE_SHIPPING_FROM ? COLISSIMO_FEE : 0;
  // Conversion : 100pts = 10€, 250pts = 30€, 500pts = 75€. Simplifié : 1pt = 0.10€ jusqu'à atteindre un palier.
  const loyaltyDiscount = Math.min(pointsToUse * 0.10, subtotal);
  const total = Math.max(0, subtotal + deliveryFee - loyaltyDiscount);

  const lookupLoyalty = async () => {
    if (!form.loyalty_email) return;
    try {
      const { data } = await axios.get(`${API}/loyalty/balance?email=${encodeURIComponent(form.loyalty_email)}`);
      setLoyaltyBalance(data);
      toast.success(`✨ ${data.points} pts disponibles · ${data.current_reward_eur}€ convertibles`);
    } catch (e) {
      setLoyaltyBalance(null);
      toast.error('Aucun compte fidélité pour cet email');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_cgv) { toast.error('Merci d\'accepter les CGV'); return; }
    if (!items.length) { toast.error('Panier vide'); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ sku: i.sku, label: i.label, unit_price: i.unit_price, qty: i.qty })),
        customer_name: form.customer_name, customer_email: form.customer_email,
        customer_phone: form.customer_phone || null,
        delivery_mode: form.delivery_mode,
        delivery_address: form.delivery_mode === 'colissimo' ? form.delivery_address : null,
        delivery_city: form.delivery_mode === 'colissimo' ? form.delivery_city : null,
        delivery_zip: form.delivery_mode === 'colissimo' ? form.delivery_zip : null,
        delivery_fee: deliveryFee,
        loyalty_email: loyaltyBalance ? form.loyalty_email : null,
        loyalty_points_used: pointsToUse,
        loyalty_discount: loyaltyDiscount,
        subtotal, total,
        notes: form.notes || null,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      // Sauvegarder l'ID de commande avant de vider le panier
      const orderId = data.id;
      clear();
      if (data.checkout_url) {
        toast.success(`Commande ${data.order_number} créée · Redirection vers le paiement…`);
        // Petite pause pour que l'utilisateur voie le toast
        setTimeout(() => { window.location.href = data.checkout_url; }, 800);
      } else {
        // Fallback : pas de lien Square — on affiche la page de confirmation manuelle
        toast.warning(`Commande ${data.order_number} enregistrée. Nous vous contactons sous 24h pour le paiement.`);
        nav(`/commande/${orderId}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création de commande');
    } finally { setLoading(false); }
  };

  if (!items.length) {
    return (
      <div className="min-h-screen bg-[#FDFAF4] py-20 px-4 text-center">
        <p className="text-[#332724]/70 mb-4">Votre panier est vide.</p>
        <Link to="/boutique" className="btn-primary px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em]">Voir la boutique</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFAF4] py-12 px-4">
      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        <form onSubmit={submit} className="space-y-6" data-testid="checkout-form">
          <h1 className="text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>Finalisez votre commande</h1>

          <section className="bg-white rounded-2xl p-5 border border-[#B8922A]/20">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-3">1 · Vos coordonnées</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Prénom et nom *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="checkout-name" />
              <input required type="email" placeholder="Email *" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="checkout-email" />
              <input type="tel" placeholder="Téléphone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="p-2.5 border border-[#B8922A]/30 rounded-lg text-sm md:col-span-2" />
            </div>
          </section>

          <section className="bg-white rounded-2xl p-5 border border-[#B8922A]/20">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-3">2 · Mode de retrait</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className={`p-4 rounded-xl border-2 cursor-pointer ${form.delivery_mode === 'click_collect' ? 'border-[#1E7A6A] bg-[#1E7A6A]/8' : 'border-[#B8922A]/30 bg-white'}`}>
                <input type="radio" name="del" value="click_collect" checked={form.delivery_mode === 'click_collect'} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} className="hidden" data-testid="delivery-click-collect" />
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#1E7A6A] mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-[#5C2018]">Click & Collect</p>
                    <p className="text-xs text-[#332724]/70 mt-1">Retrait gratuit · 14 Av. Sergent Bobillot, Béziers</p>
                    <p className="text-xs text-[#1E7A6A] font-medium mt-1">Gratuit</p>
                  </div>
                </div>
              </label>
              <label className={`p-4 rounded-xl border-2 cursor-pointer ${form.delivery_mode === 'colissimo' ? 'border-[#1E7A6A] bg-[#1E7A6A]/8' : 'border-[#B8922A]/30 bg-white'}`}>
                <input type="radio" name="del" value="colissimo" checked={form.delivery_mode === 'colissimo'} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} className="hidden" data-testid="delivery-colissimo" />
                <div className="flex items-start gap-3">
                  <Truck size={20} className="text-[#B8922A] mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-[#5C2018]">Livraison Colissimo</p>
                    <p className="text-xs text-[#332724]/70 mt-1">3-5 jours ouvrés · France métropolitaine</p>
                    <p className="text-xs text-[#B8922A] font-medium mt-1">{subtotal >= FREE_SHIPPING_FROM ? 'Offerte (commande > 60€)' : `${COLISSIMO_FEE}€ · offerte dès 60€`}</p>
                  </div>
                </div>
              </label>
            </div>
            {form.delivery_mode === 'colissimo' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <input required placeholder="Adresse *" value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="md:col-span-3 p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="checkout-address" />
                <input required placeholder="Code postal *" value={form.delivery_zip} onChange={(e) => setForm({ ...form, delivery_zip: e.target.value })} className="p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" />
                <input required placeholder="Ville *" value={form.delivery_city} onChange={(e) => setForm({ ...form, delivery_city: e.target.value })} className="md:col-span-2 p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" />
              </div>
            )}
          </section>

          <section className="bg-white rounded-2xl p-5 border border-[#B8922A]/20">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-3 flex items-center gap-2"><Award size={14} /> 3 · Utiliser mes points fidélité (facultatif)</h2>
            <div className="flex gap-2">
              <input type="email" placeholder="Email de votre compte fidélité" value={form.loyalty_email} onChange={(e) => setForm({ ...form, loyalty_email: e.target.value })} className="flex-1 p-2.5 border border-[#B8922A]/30 rounded-lg text-sm" data-testid="checkout-loyalty-email" />
              <button type="button" onClick={lookupLoyalty} className="bg-[#1E7A6A] text-white px-4 rounded-lg text-xs uppercase tracking-[0.15em]">Vérifier</button>
            </div>
            {loyaltyBalance && (
              <div className="mt-3 p-3 bg-[#1E7A6A]/10 rounded-lg">
                <p className="text-sm">Bonjour {loyaltyBalance.name} · <strong>{loyaltyBalance.points} pts disponibles</strong></p>
                <label className="block mt-2 text-xs">
                  Points à utiliser (max {Math.min(loyaltyBalance.points, Math.floor(subtotal * 10))})
                  <input type="number" min="0" max={Math.min(loyaltyBalance.points, Math.floor(subtotal * 10))} value={pointsToUse} onChange={(e) => setPointsToUse(Math.max(0, parseInt(e.target.value || 0, 10)))} className="w-full p-2 border border-[#B8922A]/30 rounded mt-1" data-testid="checkout-points-input" />
                </label>
                {pointsToUse > 0 && <p className="text-xs text-[#1E7A6A] mt-1">→ {loyaltyDiscount.toFixed(2)}€ de réduction</p>}
              </div>
            )}
          </section>

          <textarea placeholder="Notes (cadeau, demande spéciale…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full p-3 border border-[#B8922A]/30 rounded-lg text-sm resize-none" />

          <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#B8922A]/20">
            <input type="checkbox" checked={form.accept_cgv} onChange={(e) => setForm({ ...form, accept_cgv: e.target.checked })} className="mt-1 accent-[#1E7A6A]" data-testid="checkout-cgv" />
            <span className="text-xs text-[#332724]">J'accepte les <Link to="/cgv" target="_blank" className="text-[#5C2018] underline">CGV</Link> incluant les modalités de Click & Collect et de livraison Colissimo (Harmony non responsable du transport La Poste).</span>
          </label>

          <button type="submit" disabled={loading} className="w-full btn-primary px-6 py-4 rounded-full text-xs uppercase tracking-[0.2em] disabled:opacity-50" data-testid="checkout-submit">
            <Lock size={14} className="inline mr-2" />{loading ? 'Enregistrement…' : `Valider ma commande · ${total.toFixed(2)}€`}
          </button>
        </form>

        <aside className="bg-white rounded-2xl p-5 border border-[#B8922A]/30 h-fit lg:sticky lg:top-4">
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-3">Récapitulatif</h3>
          <div className="space-y-2 mb-4">
            {items.map((i) => (
              <div key={i.sku} className="flex justify-between text-xs">
                <span>{i.label} × {i.qty}</span>
                <span className="font-medium">{(i.unit_price * i.qty).toFixed(2)}€</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 pt-3 border-t border-[#B8922A]/15 text-xs">
            <div className="flex justify-between"><span>Sous-total</span><span>{subtotal.toFixed(2)}€</span></div>
            <div className="flex justify-between"><span>Livraison</span><span>{deliveryFee === 0 ? 'Gratuit' : `${deliveryFee.toFixed(2)}€`}</span></div>
            {loyaltyDiscount > 0 && <div className="flex justify-between text-[#1E7A6A]"><span>Points fidélité</span><span>-{loyaltyDiscount.toFixed(2)}€</span></div>}
          </div>
          <div className="flex justify-between mt-3 pt-3 border-t border-[#B8922A]/30">
            <span className="font-medium">Total</span>
            <span className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{total.toFixed(2)}€</span>
          </div>
        </aside>
      </div>
    </div>
  );
};

export const OrderConfirmationPage = () => {
  const [order, setOrder] = useState(null);
  const id = window.location.pathname.split('/').pop();
  React.useEffect(() => {
    axios.get(`${API}/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {});
  }, [id]);
  if (!order) return <div className="min-h-screen flex items-center justify-center text-[#5C2018]">Chargement…</div>;
  const pointsGagnes = Math.floor(order.total || 0);
  return (
    <div className="min-h-screen bg-[#FDFAF4] py-12 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 border border-[#B8922A]/30">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Commande enregistrée</p>
        <h1 className="text-3xl text-[#5C2018] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>Merci {order.customer_name.split(' ')[0]} !</h1>
        <p className="text-sm text-[#332724] mb-5">Référence : <strong>{order.order_number}</strong></p>
        <div className="bg-[#FDFAF4] rounded-lg p-4 mb-4 text-sm">
          <p className="text-[#332724]/80 mb-2">
            <strong>Mode de retrait :</strong>{' '}
            {order.delivery_mode === 'click_collect'
              ? '🏪 Click & Collect en boutique (14 Av. Sergent Bobillot, 34500 Béziers)'
              : `📦 Livraison Colissimo à ${order.delivery_city}`}
          </p>
          <p className="text-xs italic text-[#332724]/60">
            {order.delivery_mode === 'click_collect'
              ? 'Aucun remboursement en cas de non-retrait. Vous êtes notifié(e) dès que la commande est prête.'
              : 'Délai 3-5 jours ouvrés. Harmony non responsable du transport (La Poste).'}
          </p>
        </div>
        <p className="text-sm text-[#332724]/80 mb-3">Total réglé : <strong className="text-[#5C2018] text-lg">{order.total.toFixed(2)}€</strong></p>
        <div className="bg-[#B8922A]/10 border border-[#B8922A]/40 rounded-lg p-4 mb-4 flex items-center gap-3">
          <span className="text-2xl">🎁</span>
          <div className="flex-1">
            <p className="text-sm text-[#5C2018] font-medium">+{pointsGagnes} points fidélité gagnés</p>
            <p className="text-[11px] text-[#332724]/70">Crédités automatiquement à la confirmation du paiement. Utilisables dès votre prochaine commande (10 pts = 1€ de réduction).</p>
          </div>
        </div>
        <div className="bg-[#1E7A6A]/10 border border-[#1E7A6A] rounded-lg p-4 mb-4">
          <p className="text-xs text-[#332724]"><strong>✅ Paiement reçu via Square.</strong> Un reçu Square vous a été envoyé par email. Vous recevrez aussi une confirmation Maison Harmony avec les instructions de retrait/livraison sous quelques minutes.</p>
        </div>
        <Link to="/boutique" className="block w-full btn-primary px-6 py-3 rounded-full text-center text-xs uppercase tracking-[0.2em]">Retour à la boutique</Link>
      </div>
    </div>
  );
};

export default CartPage;
